import React__default, { useRef, useEffect, useCallback } from 'react';
import { Box } from '@vp/swan';
import { ButtonOptions } from '../options/button-options/button-options.mjs';
import { DropdownOptions } from '../options/dropdown-options/dropdown-options.mjs';
import { ColorSwatchOptions } from '../options/color-swatch-options/color-swatch-options.mjs';
import { ProductOptionProvider } from './context/product-context-provider.mjs';
import './context/product-option-context.mjs';
import { useProductOptionContext } from './context/use-product-option-context.mjs';
import { ProductOptionDebug } from './components/product-option-debug.mjs';
import { OptionNotification } from '../option-notification/option-notification.mjs';
import { TextboxOptions } from '../options/textbox-options/textbox-options.mjs';
import { QuantityListOptions } from '../options/quantity-list-options/quantity-list-options.mjs';
import { isProductOptionModelList } from '../../types/helpers/isProductOptionModelList.mjs';
import { useInView } from '../../../../../../react-intersection-observer/index.mjs';

const ProductOptionInner = ({ tenant, currency, fractionDigits, locale, showIncompatibleChoices = false, onChanged, onShowAllQuantities, hasSampleQuantity, uiDensityQuantityUpdate, }) => {
    const { productOption: decoratedProductOption, skin, priceDisplayMode, hideImages, hideDescriptions, savingsFormat, } = useProductOptionContext();
    const onChoiceChanged = useCallback((event) => {
        // If an event is not 'explicitly' invalid than assume it has violated no UI restrictions and is valid
        onChanged?.({
            optionKey: decoratedProductOption.key,
            isValid: event.isValid ?? true,
            ...event,
        });
    }, [decoratedProductOption.key, onChanged]);
    const sharedOptionProps = {
        showIncompatibleChoices: showIncompatibleChoices,
        tenant: tenant,
        currency: currency,
        fractionDigits: fractionDigits,
        locale: locale,
        priceDisplayMode,
        hideImages,
        hideDescriptions,
        skin,
        savingsFormat,
        uiDensityQuantityUpdate,
    };
    const productOptionModelList = isProductOptionModelList(decoratedProductOption);
    return (React__default.createElement(Box, null,
        productOptionModelList && decoratedProductOption.style === "buttons" && (React__default.createElement(ButtonOptions, { ...sharedOptionProps, option: decoratedProductOption, onChanged: onChoiceChanged })),
        productOptionModelList && decoratedProductOption.style === "dropdown" && (React__default.createElement(DropdownOptions, { ...sharedOptionProps, option: decoratedProductOption, onChanged: onChoiceChanged })),
        productOptionModelList && decoratedProductOption.style === "color-swatch" && (React__default.createElement(ColorSwatchOptions, { ...sharedOptionProps, option: decoratedProductOption, onChanged: onChoiceChanged })),
        productOptionModelList && decoratedProductOption.style === "quantity-list" && (React__default.createElement(QuantityListOptions, { ...sharedOptionProps, option: decoratedProductOption, onChanged: onChoiceChanged, onShowAllQuantities: onShowAllQuantities, hasSampleQuantity: hasSampleQuantity })),
        !productOptionModelList && decoratedProductOption.style === "textbox" && (React__default.createElement(TextboxOptions, { ...sharedOptionProps, option: decoratedProductOption, onChanged: onChoiceChanged, isDisabled: false }))));
};
const ProductOption = (props) => {
    const { productOption, skin = "wide", hideImages, hideDescriptions, savingsFormat, debug, showFormErrors, priceDisplayMode, uiDensityQuantityUpdate, ...rest } = props;
    const [optionNotificationRef, optionNotificationInView] = useInView({
        threshold: 1,
        triggerOnce: true,
    });
    const optionNotificationContainer = useRef(null);
    const showOptionNotification = isProductOptionModelList(productOption) &&
        productOption.previouslyUnselectedOptions &&
        productOption.previouslyUnselectedOptions?.optionCausingUnselect?.attributeKey ===
            productOption.key &&
        productOption.previouslyUnselectedOptions?.optionsUnselected.length > 0;
    useEffect(() => {
        if (!optionNotificationInView && showOptionNotification) {
            optionNotificationContainer.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [showOptionNotification, optionNotificationInView, optionNotificationContainer]);
    return (React__default.createElement(ProductOptionProvider, { productOption: productOption, skin: skin, priceDisplayMode: priceDisplayMode, hideImages: hideImages, hideDescriptions: hideDescriptions, savingsFormat: savingsFormat, showFormErrors: showFormErrors, uiDensityQuantityUpdate: uiDensityQuantityUpdate },
        React__default.createElement("div", { ref: optionNotificationContainer }, showOptionNotification ? (React__default.createElement(OptionNotification, { key: `option-notif-${productOption.key}`, notificationOptions: productOption.previouslyUnselectedOptions, ref: optionNotificationRef })) : (React__default.createElement("div", { ref: optionNotificationRef }))),
        React__default.createElement(ProductOptionInner, { ...rest }),
        debug && React__default.createElement(ProductOptionDebug, null)));
};

export { ProductOption };
//# sourceMappingURL=product-option.mjs.map
